import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AdminAPI from '../services/adminApi';

// Async thunks
export const fetchSchema = createAsyncThunk(
  'admin/fetchSchema',
  async (_, { rejectWithValue }) => {
    try {
      return await AdminAPI.getSchema();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTableRows = createAsyncThunk(
  'admin/fetchTableRows',
  async (tableName, { rejectWithValue }) => {
    try {
      const response = await AdminAPI.listRows(tableName);
      return { tableName, ...response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createRow = createAsyncThunk(
  'admin/createRow',
  async ({ tableName, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await AdminAPI.createRow(tableName, data);
      // Refresh table data after create
      dispatch(fetchTableRows(tableName));
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateRow = createAsyncThunk(
  'admin/updateRow',
  async ({ tableName, rowId, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await AdminAPI.updateRow(tableName, rowId, data);
      // Refresh table data after update
      dispatch(fetchTableRows(tableName));
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchForeignKeyOptions = createAsyncThunk(
  'admin/fetchForeignKeyOptions',
  async (_, { getState, rejectWithValue }) => {
    try {
      const fkConfig = getState().admin.schema?.fk_config || {};
      const tables = [...new Set(Object.values(fkConfig).map((c) => c.table))];
      const results = await Promise.all(tables.map((t) => AdminAPI.listRows(t)));
      return Object.fromEntries(tables.map((t, i) => [t, results[i].rows || []]));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRoutines = createAsyncThunk(
  'admin/fetchRoutines',
  async (_, { rejectWithValue }) => {
    try {
      return await AdminAPI.listRoutines();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRoutineDetail = createAsyncThunk(
  'admin/fetchRoutineDetail',
  async (oid, { rejectWithValue }) => {
    try {
      return await AdminAPI.getRoutine(oid);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveRoutine = createAsyncThunk(
  'admin/saveRoutine',
  async ({ oid, definition }, { rejectWithValue, dispatch }) => {
    try {
      const response = await AdminAPI.updateRoutine(oid, definition);
      dispatch(fetchRoutines());
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteRow = createAsyncThunk(
  'admin/deleteRow',
  async ({ tableName, rowId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await AdminAPI.deleteRow(tableName, rowId);
      // Refresh table data after delete
      dispatch(fetchTableRows(tableName));
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  schema: null,
  selectedTable: 'brands',
  tableData: {},
  foreignKeyOptions: {},
  loading: false,
  error: null,

  // Table modal state
  isRowModalOpen: false,
  editingRow: null,
  isDeleteConfirmOpen: false,
  deletingRowId: null,

  // Routine state
  routines: [],
  isRoutineModalOpen: false,
  editingRoutine: null
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setSelectedTable: (state, action) => {
      state.selectedTable = action.payload;
    },
    openRowModal: (state, action) => {
      state.isRowModalOpen = true;
      state.editingRow = action.payload || null;
    },
    closeRowModal: (state) => {
      state.isRowModalOpen = false;
      state.editingRow = null;
    },
    openDeleteConfirm: (state, action) => {
      state.isDeleteConfirmOpen = true;
      state.deletingRowId = action.payload;
    },
    closeDeleteConfirm: (state) => {
      state.isDeleteConfirmOpen = false;
      state.deletingRowId = null;
    },
    openRoutineModal: (state) => {
      state.isRoutineModalOpen = true;
    },
    closeRoutineModal: (state) => {
      state.isRoutineModalOpen = false;
      state.editingRoutine = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch schema
      .addCase(fetchSchema.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchema.fulfilled, (state, action) => {
        state.loading = false;
        state.schema = action.payload;
      })
      .addCase(fetchSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch foreign key options
      .addCase(fetchForeignKeyOptions.fulfilled, (state, action) => {
        state.foreignKeyOptions = action.payload;
      })
      // Fetch table rows
      .addCase(fetchTableRows.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTableRows.fulfilled, (state, action) => {
        state.loading = false;
        state.tableData[action.payload.tableName] = {
          rows: action.payload.rows,
          total: action.payload.total
        };
      })
      .addCase(fetchTableRows.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create row
      .addCase(createRow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRow.fulfilled, (state) => {
        state.loading = false;
        state.isRowModalOpen = false;
        state.editingRow = null;
      })
      .addCase(createRow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update row
      .addCase(updateRow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRow.fulfilled, (state) => {
        state.loading = false;
        state.isRowModalOpen = false;
        state.editingRow = null;
      })
      .addCase(updateRow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch routines
      .addCase(fetchRoutines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoutines.fulfilled, (state, action) => {
        state.loading = false;
        state.routines = action.payload.routines;
      })
      .addCase(fetchRoutines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch routine detail
      .addCase(fetchRoutineDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoutineDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.editingRoutine = action.payload;
        state.isRoutineModalOpen = true;
      })
      .addCase(fetchRoutineDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save routine
      .addCase(saveRoutine.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveRoutine.fulfilled, (state, action) => {
        state.loading = false;
        state.isRoutineModalOpen = false;
        state.editingRoutine = action.payload;
      })
      .addCase(saveRoutine.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete row
      .addCase(deleteRow.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRow.fulfilled, (state) => {
        state.loading = false;
        state.isDeleteConfirmOpen = false;
        state.deletingRowId = null;
      })
      .addCase(deleteRow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Selectors
export const selectSchema = (state) => state.admin.schema;
export const selectForeignKeyOptions = (state) => state.admin.foreignKeyOptions;
export const selectSelectedTable = (state) => state.admin.selectedTable;
export const selectTableData = (state) => state.admin.tableData;
export const selectCurrentTableData = (state) => state.admin.tableData[state.admin.selectedTable];
export const selectLoading = (state) => state.admin.loading;
export const selectError = (state) => state.admin.error;
export const selectIsRowModalOpen = (state) => state.admin.isRowModalOpen;
export const selectEditingRow = (state) => state.admin.editingRow;
export const selectIsDeleteConfirmOpen = (state) => state.admin.isDeleteConfirmOpen;
export const selectDeletingRowId = (state) => state.admin.deletingRowId;
export const selectRoutines = (state) => state.admin.routines;
export const selectIsRoutineModalOpen = (state) => state.admin.isRoutineModalOpen;
export const selectEditingRoutine = (state) => state.admin.editingRoutine;

export const {
  setSelectedTable,
  openRowModal,
  closeRowModal,
  openDeleteConfirm,
  closeDeleteConfirm,
  openRoutineModal,
  closeRoutineModal,
  clearError
} = adminSlice.actions;

export default adminSlice.reducer;
